import { useState, useMemo } from "react";
import PageShell from "@/components/PageShell";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, CheckCircle, WarningOctagon, ArrowRight } from "@phosphor-icons/react";

const CLIENT_FIELDS = ["name", "industry", "company_size", "hq_location", "website", "tier", "relationship_health", "relationship_owner", "annual_revenue", "strategic_context"];
const ENGAGEMENT_FIELDS = ["name", "client_name", "engagement_type", "status", "stage", "partner_lead", "start_date", "target_end_date", "budget", "billing_model", "scope_summary"];

export default function Imports() {
  const [kind, setKind] = useState("clients");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mapping, setMapping] = useState({});

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const resp = await fetch(`${API}/imports/preview`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${resp.status})`);
      }
      const data = await resp.json();
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      toast.success(`Parsed ${data.total_rows} row(s) from ${file.name}`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remap = (field, header) => {
    const next = { ...mapping, [field]: header === "__none__" ? null : header };
    setMapping(next);
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      // Rebuild rows from headers based on the (potentially remapped) mapping
      const rows = preview.preview_rows;
      const { data } = await api.post("/imports/commit", { kind, mapping, rows });
      setResult(data);
      toast.success(`Import complete: ${data.created} created · ${data.updated} updated · ${data.skipped} skipped`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  // Both list of target fields (derived from kind) and the mapped subset are memoized
  // so a keystroke on any select doesn't re-derive both on every row of the preview table.
  const targetFields = useMemo(() => (kind === "clients" ? CLIENT_FIELDS : ENGAGEMENT_FIELDS), [kind]);
  const mappedFields = useMemo(() => targetFields.filter((f) => mapping[f]), [targetFields, mapping]);

  return (
    <PageShell size="narrow" testid="imports-page">
      <div className="mb-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">Onboarding</div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Bulk import</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Drop in your existing Excel/CSV client &amp; engagement trackers. YadidOS auto-detects columns; you tweak the mapping, then commit.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="border border-border bg-card p-6">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">1 · Choose data</div>
          <Select value={kind} onValueChange={(v) => { setKind(v); setPreview(null); setResult(null); }}>
            <SelectTrigger data-testid="import-kind-select" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="clients">Partner Organizations</SelectItem>
              <SelectItem value="engagements">Engagements</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[0.6875rem] text-muted-foreground mt-2 leading-relaxed">
            {kind === "clients"
              ? "Header row must include at least a Name column. Tier/health snap to nearest allowed value."
              : "Each row must have engagement name + client name (client must already exist in YadidOS)."}
          </p>
        </div>

        <div className="border border-border bg-card p-6 md:col-span-2">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">2 · Upload spreadsheet (.xlsx, ≤5MB)</div>
          <label className="block cursor-pointer border-2 border-dashed border-primary/50 bg-secondary/20 px-6 py-10 text-center hover:bg-primary/5 transition" data-testid="import-dropzone">
            <UploadSimple size={28} weight="bold" className="mx-auto text-primary" />
            <div className="mt-2 text-sm">
              {busy ? "Parsing…" : "Click or drop an .xlsx file"}
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
        </div>
      </div>

      {preview && !result && (
        <div className="border border-border bg-card p-6" data-testid="import-mapping">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">3 · Confirm column mapping</div>
            <div className="text-[0.6875rem] font-mono text-muted-foreground">Showing {preview.preview_rows.length} of {preview.total_rows} rows</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {targetFields.map((field) => (
              <div key={field}>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                  {field} <span className="text-muted-foreground">{field === "name" ? "· required" : ""}</span>
                </div>
                <Select value={mapping[field] || "__none__"} onValueChange={(v) => remap(field, v)}>
                  <SelectTrigger data-testid={`map-${field}`} className="rounded-none bg-secondary/40 h-9 text-xs"><SelectValue placeholder="— none —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="overflow-auto border border-border/50">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40">
                <tr>
                  {mappedFields.map((f) => (
                    <th key={f} className="text-left px-3 py-2 font-mono uppercase text-[0.625rem] tracking-widest text-muted-foreground border-b border-border">{f}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview_rows.slice(0, 10).map((r, i) => (
                  <tr key={r.__row_id || i} className="border-b border-border/50">
                    {mappedFields.map((f) => (
                      <td key={f} className="px-3 py-2 whitespace-nowrap">{String(r[f] ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              data-testid="import-commit-btn"
              onClick={commit}
              disabled={busy}
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {busy ? "Importing…" : `Import ${preview.total_rows} rows`} <ArrowRight size={14} weight="bold" className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="border border-border bg-card p-6" data-testid="import-result">
          <div className="flex items-start gap-3">
            <CheckCircle size={22} weight="fill" className="text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg font-semibold">Import finished</div>
              <div className="text-sm text-muted-foreground mt-1">
                <span className="text-primary font-mono">{result.created}</span> created ·{" "}
                <span className="text-primary font-mono">{result.updated}</span> updated ·{" "}
                <span className="text-primary font-mono">{result.skipped}</span> skipped
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-4">
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-destructive mb-1 flex items-center gap-1">
                    <WarningOctagon size={12} weight="fill" /> Issues
                  </div>
                  <ul className="text-xs font-mono text-muted-foreground space-y-1">
                    {result.errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
